import axios, { AxiosResponse } from 'axios';
import {
  User,
  Category,
  Product,
  Video,
  CreateVideoData,
  UpdateVideoData,
  CreateVideoResponse,
  ArContent,
  TestQuestion,
  UserActivity,
  Certificate,
  UserProgress,
  AuthResponse,
  LoginCredentials,
  RegisterData,
  DashboardStats,
  CreateUserForm,
  Notification,
  UserGroup,
  PaginatedResponse,
  ApiResponse,
  CreateSubcategoryData,
  CreateSubcategoryResponse,
  UpdateSubcategoryData,
  CreateProductData,
  CreateProductResponse,
  UpdateProductData
} from '../types';

// NUEVOS TIPOS para subcategorías jerárquicas
export interface SubcategoryHierarchy {
  id: string;
  categoryId: string;
  parentSubcategoryId?: string;
  name: string;
  size: string;
  iconUrl?: string;
  level: number;
  hasChildren: boolean;
  hasProducts: boolean;
  isLeaf: boolean;
  productCount: number;
  childrenCount?: number;
  children: SubcategoryHierarchy[];
  products: Product[];
  parent?: SubcategoryHierarchy;
  fullPath?: string;
}

export interface BreadcrumbItem {
  id: string;
  name: string;
}

export interface SubcategoryBreadcrumb {
  names: string[];
  ids: string[];
  depth: number;
}

export interface NotificationForm {
  title: string;
  body: string;
  imageUrl?: string;
  actionButtonText?: string;
  actionButtonUrl?: string;
  recipients: {
    type: 'all' | 'group' | 'users';
    groupId?: string;
    userCedulas?: string[]; // Cambiar de userIds a userCedulas
  };
}

// Configuración base de axios - CORREGIDO para Vite
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/* =========================
   LIMITADOR DE PETICIONES
   - Controla concurrencia y QPS para evitar llegar al rate-limit del backend
   ========================= */
type Pending = { resolve: (cfg: any) => void; reject: (err: any) => void; cfg: any };
const queue: Pending[] = [];

const MAX_CONCURRENT = 6;   // solicitudes simultáneas
const MAX_PER_WINDOW = 40;  // solicitudes por ventana
const WINDOW_MS = 10_000;   // ventana 10s

let inFlight = 0;
const timestamps: number[] = [];
const now = () => Date.now();

function canSend() {
  const cutoff = now() - WINDOW_MS;
  while (timestamps.length && timestamps[0] < cutoff) timestamps.shift();
  return inFlight < MAX_CONCURRENT && timestamps.length < MAX_PER_WINDOW;
}

function schedule() {
  while (queue.length && canSend()) {
    const pending = queue.shift()!;
    inFlight++;
    timestamps.push(now());
    pending.resolve(pending.cfg);
  }
}

function enqueue(cfg: any) {
  return new Promise<any>((resolve, reject) => {
    queue.push({ resolve, reject, cfg });
    schedule();
  });
}

function release() {
  inFlight = Math.max(0, inFlight - 1);
  setTimeout(schedule, 0);
}

/* =========================
   INTERCEPTORES
   ========================= */

// Interceptor para agregar token de autenticación (PRIMERO)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de request para LIMITADOR (cola de envío)
api.interceptors.request.use(async (config) => {
  if (canSend()) {
    inFlight++;
    timestamps.push(now());
    return config;
  }
  return enqueue(config);
});

// Flag para evitar múltiples redirects simultáneos
let isRedirectingToLogin = false;

// ÚNICO interceptor de response (con 401/429 + release del limitador)
api.interceptors.response.use(
  (response) => {
    release();
    return response;
  },
  async (error) => {
    release();
    const status = error?.response?.status;

    // 401: evitar tormenta de redirects
    if (status === 401 && !isRedirectingToLogin) {
      const currentPath = window.location.pathname;
      const isPublicPage = ['/login', '/register'].includes(currentPath);

      if (!isPublicPage) {
        isRedirectingToLogin = true;
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }

      return Promise.reject(error);
    }

    // 429: respeta Retry-After si el backend lo envía
    if (status === 429) {
      const retryAfter = Number(error.response?.headers?.['retry-after']);
      if (retryAfter && retryAfter > 0) {
        await new Promise(res => setTimeout(res, retryAfter * 1000));
      } else {
        // pequeño backoff por defecto
        await new Promise(res => setTimeout(res, 1500));
      }
    }

    return Promise.reject(error);
  }
);

// Servicios de autenticación (sin cambios)
export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (data: RegisterData): Promise<ApiResponse<User>> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  verifyToken: async (token: string): Promise<{ valid: boolean; user?: User }> => {
    const response = await api.post('/auth/verify-token', { token });
    return response.data;
  },

  changePassword: async (data: {
    token: string;
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse> => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  forgotPassword: async (cedula: string): Promise<ApiResponse> => {
    const response = await api.post('/auth/forgot-password', { cedula });
    return response.data;
  },
};

// Servicios de usuario (sin cambios)
export const userService = {
  getProfile: async (): Promise<{ user: User }> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  updateProfile: async (data: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  getActivities: async (params?: {
    page?: number;
    limit?: number;
    categoryId?: string;
    activityType?: string;
  }): Promise<PaginatedResponse<UserActivity>> => {
    const response = await api.get('/users/activities', { params });
    return response.data;
  },

  getCertificates: async (): Promise<{ certificates: Certificate[] }> => {
    const response = await api.get('/users/certificates');
    return response.data;
  },

  getProgress: async (categoryId: string): Promise<{ progress: UserProgress }> => {
    const response = await api.get(`/users/progress/${categoryId}`);
    return response.data;
  },
};

// Servicios de categorías - ACTUALIZADOS
export const categoryService = {
  getAll: async (): Promise<{ categories: Category[] }> => {
    const response = await api.get('/categories');
    return response.data;
  },

  getById: async (id: string): Promise<{ category: Category & { subcategories: SubcategoryHierarchy[] } }> => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  getSubcategories: async (id: string, options?: {
    includeChildren?: boolean;
  }): Promise<{ subcategories: SubcategoryHierarchy[] }> => {
    const params = {
      includeChildren: options?.includeChildren ? 'true' : 'false'
    };
    const response = await api.get(`/categories/${id}/subcategories`, { params });
    return response.data;
  },

  // NUEVO: Obtener subcategorías hijas de una subcategoría específica
  getSubcategoryChildren: async (categoryId: string, subcategoryId: string): Promise<{ subcategories: SubcategoryHierarchy[] }> => {
    const response = await api.get(`/categories/${categoryId}/subcategories/${subcategoryId}/children`);
    return response.data;
  },

  getVideos: async (id: string): Promise<{ videos: Video[] }> => {
    const response = await api.get(`/categories/${id}/videos`);
    return response.data;
  },

  getArContent: async (id: string): Promise<{ arContents: ArContent[] }> => {
    const response = await api.get(`/categories/${id}/ar`);
    return response.data;
  },

  getTestQuestions: async (id: string): Promise<{ questions: TestQuestion[] }> => {
    const response = await api.get(`/categories/${id}/test`);
    return response.data;
  },
};

// NUEVOS: Servicios específicos para subcategorías
export const subcategoryService = {
  getById: async (id: string, options?: {
    includeTree?: boolean;
  }): Promise<{ subcategory: SubcategoryHierarchy }> => {
    const params = {
      includeTree: options?.includeTree ? 'true' : 'false'
    };
    const response = await api.get(`/subcategories/${id}`, { params });
    return response.data;
  },

  getChildren: async (id: string): Promise<{ subcategories: SubcategoryHierarchy[] }> => {
    const response = await api.get(`/subcategories/${id}/children`);
    return response.data;
  },

  getBreadcrumb: async (id: string): Promise<{ breadcrumb: SubcategoryBreadcrumb }> => {
    const response = await api.get(`/subcategories/${id}/breadcrumb`);
    return response.data;
  },

  getTreeInfo: async (id: string): Promise<{
    treeInfo: {
      totalDescendants: number;
      maxDepth: number;
      isRequestedNode: boolean;
    }
  }> => {
    const response = await api.get(`/subcategories/${id}/tree-info`);
    return response.data;
  },

  // MÉTODOS para AdminContent - CORREGIDOS
  // create: async (data: CreateSubcategoryData): Promise<CreateSubcategoryResponse> => {
  //   const response = await api.post('/admin/subcategories', data);
  //   return response.data;
  // },

  create: async (data: CreateSubcategoryData): Promise<CreateSubcategoryResponse> => {
    // Caso 1️⃣: si hay archivo, usar /api/upload/subcategory
    if (data.iconFile instanceof File) {
      const form = new FormData();
      form.append('categoryId', data.categoryId);
      form.append('name', data.name);
      form.append('size', data.size);
      if (data.parentSubcategoryId) form.append('parentSubcategoryId', data.parentSubcategoryId);
      form.append('icon', data.iconFile); // campo esperado por backend

      // console.log('[subcategoryService.create] Subiendo ícono vía /api/upload/subcategory');
      const response = await api.post('/upload/subcategory', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // backend devuelve { uploadedIcon, subcategory }
      return response.data;
    }

    // Caso 2️⃣: sin archivo, usar el endpoint existente
    // console.log('[subcategoryService.create] Creando subcategoría vía /admin/subcategories (JSON)');
    const response = await api.post('/admin/subcategories', {
      categoryId: data.categoryId,
      parentSubcategoryId: data.parentSubcategoryId ?? null,
      name: data.name,
      size: data.size,
      iconUrl: data.iconUrl ?? null,
    });

    return response.data;
  },

  // update: async (id: string, data: UpdateSubcategoryData): Promise<CreateSubcategoryResponse> => {
  //   const response = await api.put(`/admin/subcategories/${id}`, data);
  //   return response.data;
  // },
  update: async (id: string, data: CreateSubcategoryData): Promise<CreateSubcategoryResponse> => {
    // Caso 1️⃣: si hay nuevo ícono → usar endpoint de upload
    if (data.iconFile instanceof File) {
      const form = new FormData();
      form.append('categoryId', data.categoryId);
      form.append('name', data.name);
      form.append('size', data.size);
      if (data.parentSubcategoryId) form.append('parentSubcategoryId', data.parentSubcategoryId);
      form.append('icon', data.iconFile);

      // console.log('[subcategoryService.update] Subiendo ícono vía /api/upload/subcategory/:id');
      const response = await api.put(`/upload/subcategory/id/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // Caso 2️⃣: sin archivo → usar PUT normal
    // console.log('[subcategoryService.update] Actualizando subcategoría vía /subcategories/:id');
    const response = await api.put(`/subcategories/${id}`, {
      categoryId: data.categoryId,
      parentSubcategoryId: data.parentSubcategoryId ?? null,
      name: data.name,
      size: data.size,
      iconUrl: data.iconUrl ?? null,
    });

    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/subcategories/${id}`);
    return response.data;
  },

  getAll: async (): Promise<{ subcategories: SubcategoryHierarchy[] }> => {
    const response = await api.get('/subcategories');
    return response.data;
  }
};

// Servicios de productos - ACTUALIZADOS
export const productService = {

  getByCategory: async (categoryId: string): Promise<{ products: Product[] }> => {
    const response = await api.get(`/products/category/${categoryId}`);
    return response.data;
  },

  getBySubcategory: async (subcategoryId: string): Promise<{ products: Product[] }> => {
    const response = await api.get(`/products/subcategory/${subcategoryId}`);
    return response.data;
  },

  getById: async (id: string): Promise<{ product: Product }> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Crear producto (con archivo o con URL)
  create: async (data: CreateProductData): Promise<CreateProductResponse> => {
    // Si viene archivo -> multipart a /api/upload/product
    if (data.imageFile instanceof File) {
      const form = new FormData();
      form.append('subcategoryId', data.subcategoryId);
      form.append('name', data.name);
      if (data.description) form.append('description', data.description);
      if (data.internalCode) form.append('internalCode', data.internalCode);
      form.append('image', data.imageFile); // backend espera "image"

      // console.log('[productService.create] Subiendo con imagen vía /api/upload/product');
      const response = await api.post('/upload/product', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Backend devuelve { uploadedImage, product }
      return response.data?.product ?? response.data;
    }

    // Si NO hay archivo -> JSON a /admin/products
    // console.log('[productService.create] Creando producto vía /admin/products (JSON)');
    const response = await api.post('/admin/products', {
      subcategoryId: data.subcategoryId,
      name: data.name,
      description: data.description ?? null,
      internalCode: data.internalCode ?? null,
      imageUrl: data.imageUrl ?? null,
    });

    return response.data;
  },

  // src/services/api.ts o src/services/productService.ts
  update: async (id: string, data: any): Promise<ApiResponse> => {
    // Caso 1️⃣: si hay archivo (nueva imagen)
    if (data.imageFile instanceof File) {
      const form = new FormData();
      form.append('subcategoryId', data.subcategoryId);
      form.append('name', data.name);
      if (data.description) form.append('description', data.description);
      if (data.internalCode) form.append('internalCode', data.internalCode);
      form.append('image', data.imageFile);

      // console.log('[productService.update] Subiendo nueva imagen vía /api/upload/product/id/:id');
      const response = await api.put(`/upload/product/id/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // Caso 2️⃣: sin archivo → PUT JSON
    // console.log('[productService.update] Actualizando producto vía /api/products/:id');
    const response = await api.put(`/products/${id}`, {
      subcategoryId: data.subcategoryId,
      name: data.name,
      description: data.description ?? null,
      internalCode: data.internalCode ?? null,
      imageUrl: data.imageUrl ?? null,
    });

    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/products/${id}`);
    return response.data;
  },

  getAll: async (): Promise<{ products: Product[] }> => {
    const response = await api.get('/products');
    return response.data;
  },
};

// Servicios de actividades (sin cambios)
export const activityService = {
  recordActivity: async (data: {
    categoryId: string;
    activityType: string;
    completed?: boolean;
    timeSpent?: number;
    metadata?: any;
  }): Promise<ApiResponse<UserActivity>> => {
    const response = await api.post('/activities', data);
    return response.data;
  },

  // 🔹 Nuevo: obtener resumen de actividades completadas
  getSummary: async (): Promise<{
    message: string;
    data: {
      video: number;
      ar: number;
      test: number;
      portfolio: number;
      totalCompleted: number;
    };
  }> => {
    // console.log('[activityService.getSummary] → llamando a /activities/summary');
    try {
      const response = await api.get('/activities/summary');
      // console.log('[activityService.getSummary] ✅ response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[activityService.getSummary] ❌ error:', error?.response || error);
      throw error;
    }
  },
};

// Servicios de certificados (sin cambios)
export const certificateService = {
  download: async (id: string): Promise<Blob> => {
    const response = await api.get(`/certificates/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Servicios de administración - ACTUALIZADOS
export const adminService = {
  getDashboard: async (): Promise<DashboardStats> => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    zone?: string;
  }): Promise<PaginatedResponse<User>> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  createUser: async (data: CreateUserForm): Promise<ApiResponse<User>> => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  bulkUploadUsers: async (file: File): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/users/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateUserStatus: async (userId: string, status: string): Promise<ApiResponse> => {
    const response = await api.patch(`/admin/users/${userId}/status`, { status });
    return response.data;
  },

  updateUser: async (userId: string, data: Partial<User> & { password?: string }): Promise<ApiResponse<User>> => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  // Eliminar usuario (admin)
  deleteUser: async (userId: string): Promise<ApiResponse> => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // Gestión de grupos (sin cambios)
  getGroups: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<UserGroup>> => {
    const response = await api.get('/admin/groups', { params });
    return response.data;
  },

  createGroup: async (data: {
    name: string;
    description?: string;
  }): Promise<ApiResponse<UserGroup>> => {
    const response = await api.post('/admin/groups', data);
    return response.data;
  },

  addUsersToGroup: async (groupId: string, userIds: string[]): Promise<ApiResponse> => {
    const response = await api.post(`/admin/groups/${groupId}/members`, { userIds });
    return response.data;
  },

  updateGroup: async (groupId: string, data: Partial<UserGroup>): Promise<ApiResponse<UserGroup>> => {
    const response = await api.put(`/admin/groups/${groupId}`, data);
    return response.data;
  },

  deleteGroup: async (groupId: string): Promise<ApiResponse> => {
    const response = await api.delete(`/admin/groups/${groupId}`);
    return response.data;
  },

  removeUserFromGroup: async (groupId: string, userId: string): Promise<ApiResponse> => {
    const response = await api.delete(`/admin/groups/${groupId}/members/${userId}`);
    return response.data;
  },

  // Gestión de contenido - ACTUALIZADO para subcategorías jerárquicas
  createSubcategory: async (data: {
    categoryId: string;
    parentSubcategoryId?: string; // NUEVO: Para subcategorías anidadas
    name: string;
    size: string;
    iconUrl?: string;
    level?: number; // NUEVO: Nivel de anidamiento
  }): Promise<ApiResponse> => {
    const response = await api.post('/admin/subcategories', data);
    return response.data;
  },

  updateSubcategory: async (id: string, data: any): Promise<ApiResponse> => {
    // Caso 1️⃣: si hay archivo (nuevo ícono)
    if (data.iconFile instanceof File) {
      const formData = new FormData();
      formData.append('categoryId', data.categoryId);
      formData.append('name', data.name);
      formData.append('size', data.size);
      if (data.parentSubcategoryId)
        formData.append('parentSubcategoryId', data.parentSubcategoryId);
      formData.append('icon', data.iconFile);

      // console.log('[subcategoryService.update] Enviando a /api/upload/subcategory/id:id');
      const response = await api.put(`/upload/subcategory/id/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // Caso 2️⃣: sin archivo, usa JSON normal
    // console.log('[subcategoryService.update] Enviando a /api/subcategories/:id');
    const response = await api.put(`/subcategories/${id}`, {
      categoryId: data.categoryId,
      parentSubcategoryId: data.parentSubcategoryId ?? null,
      name: data.name,
      size: data.size,
      iconUrl: data.iconUrl ?? null,
    });
    return response.data;
  },

  deleteSubcategory: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/admin/subcategories/${id}`);
    return response.data;
  },

  createProduct: async (data: {
    subcategoryId: string;
    name: string;
    description?: string;
    imageUrl?: string;
    internalCode?: string;
  }): Promise<ApiResponse> => {
    const response = await api.post('/admin/products', data);
    return response.data;
  },

  updateProduct: async (id: string, data: Partial<Product>): Promise<ApiResponse> => {
    const response = await api.put(`/admin/products/${id}`, data);
    return response.data;
  },

  deleteProduct: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/admin/products/${id}`);
    return response.data;
  },
};

// Servicios de notificaciones (sin cambios)
export const notificationService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Notification>> => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ notification: Notification }> => {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },

  create: async (data: NotificationForm): Promise<ApiResponse<Notification>> => {
    const response = await api.post('/notifications', data);
    return response.data;
  },

  update: async (id: string, data: Partial<NotificationForm>): Promise<ApiResponse<Notification>> => {
    const response = await api.put(`/notifications/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  getStats: async (): Promise<{
    stats: {
      sentToday: number;
      usersReached: number;
      deliveryRate: number;
      openRate: number;
    }
  }> => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },

  getUserNotifications: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Notification>> => {
    const response = await api.get('/notifications/user/inbox', { params });
    return response.data;
  },

  markAsRead: async (sendId: string): Promise<ApiResponse> => {
    const response = await api.patch(`/notifications/user/${sendId}/read`);
    return response.data;
  },

  deleteUserNotification: async (sendId: string): Promise<ApiResponse> => {
    const response = await api.delete(`/notifications/user/${sendId}`);
    return response.data;
  },
};

// Servicios de archivos (sin cambios)
export const fileService = {
  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadDocument: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('document', file);
    const response = await api.post('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Servicio Reportes
export const reportService = {
  getDashboard: async (dateRange?: string): Promise<any> => {
    const response = await api.get('/reports/dashboard', {
      params: { dateRange }
    });
    return response.data;
  },

  exportData: async (type: 'users' | 'activities' | 'certificates', dateRange?: string): Promise<any> => {
    const response = await api.get(`/reports/export/${type}`, {
      params: { dateRange }
    });
    return response.data;
  },

  exportUnified: async (dateRange?: string): Promise<{
    users: any[];
    activities: any[];
    certificates: any[];
  }> => {
    // Llamar los 3 endpoints en paralelo
    const [usersRes, activitiesRes, certificatesRes] = await Promise.all([
      api.get('/reports/export/users', { params: { dateRange } }),
      api.get('/reports/export/activities', { params: { dateRange } }),
      api.get('/reports/export/certificates', { params: { dateRange } }),
    ]);

    return {
      users: usersRes.data.data || [],
      activities: activitiesRes.data.data || [],
      certificates: certificatesRes.data.data || [],
    };
  },
};

// Agregar estos servicios de videos
export const videoService = {
  getAll: async (): Promise<{ videos: Video[] }> => {
    const response = await api.get('/videos');
    return response.data;
  },

  getByCategory: async (categoryId: string): Promise<{ videos: Video[] }> => {
    const response = await api.get(`/videos/category/${categoryId}`);
    return response.data;
  },

  getById: async (id: string): Promise<{ video: Video }> => {
    const response = await api.get(`/videos/${id}`);
    return response.data;
  },

  create: async (data: CreateVideoData): Promise<CreateVideoResponse> => {
    const response = await api.post('/videos', data);
    return response.data;
  },

  update: async (id: string, data: UpdateVideoData): Promise<CreateVideoResponse> => {
    const response = await api.put(`/videos/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/videos/${id}`);
    return response.data;
  },
};

// Servicios de admin test questions
export const testService = {
  getByCategory: async (categoryId: string): Promise<{ questions: import('../types').AdminTestQuestion[] }> => {
    const response = await api.get(`/admin/tests/${categoryId}`);
    return response.data;
  },

  create: async (data: import('../types').CreateTestQuestionData): Promise<{ question: import('../types').AdminTestQuestion }> => {
    const response = await api.post('/admin/tests', data);
    return response.data;
  },

  update: async (id: string, data: import('../types').UpdateTestQuestionData): Promise<{ question: import('../types').AdminTestQuestion }> => {
    const response = await api.put(`/admin/tests/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/tests/${id}`);
    return response.data;
  },
};

export default api;